import { isAthenaBillingCredentialError } from "../errors.ts";
import { shouldAutomaticallyImportBillingCustomers } from "../import/automatic.ts";
import { AthenaBillingReconciliationError } from "./retry.ts";

export type BillingImportExecutionMode = "embedded" | "external";

export interface BillingReconciliationSchedule {
  intervalMs: number;
  jitterMs: number;
}

export const DEFAULT_BILLING_RECONCILIATION_SCHEDULE: BillingReconciliationSchedule =
  {
    intervalMs: 600_000,
    jitterMs: 60_000,
  };

export const DEFAULT_BILLING_WEBHOOK_RECONCILIATION_SCHEDULE: BillingReconciliationSchedule =
  {
    intervalMs: 10_800_000,
    jitterMs: 300_000,
  };

function jitterDelay(jitterMs: number): number {
  if (jitterMs <= 0) {
    return 0;
  }
  return Math.floor(Math.random() * jitterMs);
}

export function logEmbeddedBillingSchedulerError(error: unknown): void {
  const payload = {
    error: error instanceof Error ? error.message : String(error),
  };
  if (isAthenaBillingCredentialError(error)) {
    console.warn("[athena-billing] skipping scheduled reconciliation", payload);
    return;
  }
  if (
    error instanceof AthenaBillingReconciliationError &&
    error.kind === "lease_lost"
  ) {
    console.warn("[athena-billing] skipping scheduled reconciliation", payload);
    return;
  }
  console.error("[athena-billing] scheduled reconciliation failed", payload);
}

export function registerEmbeddedBillingScheduler(input: {
  enabled: boolean;
  execution?: BillingImportExecutionMode;
  onError?: (error: unknown) => void;
  run: (trigger: "bootstrap" | "scheduled") => Promise<void>;
  schedule?: Partial<BillingReconciliationSchedule>;
}): { cancel(): void } | null {
  if (!input.enabled) {
    return null;
  }
  if ((input.execution ?? "embedded") !== "embedded") {
    return null;
  }
  const intervalMs =
    input.schedule?.intervalMs ??
    DEFAULT_BILLING_RECONCILIATION_SCHEDULE.intervalMs;
  const jitterMs =
    input.schedule?.jitterMs ??
    DEFAULT_BILLING_RECONCILIATION_SCHEDULE.jitterMs;
  const onError = input.onError ?? logEmbeddedBillingSchedulerError;
  let cancelled = false;
  let interval: ReturnType<typeof setInterval> | undefined;
  const runSafe = (trigger: "bootstrap" | "scheduled") => {
    void input.run(trigger).catch(onError);
  };
  const bootstrap = setTimeout(() => {
    if (cancelled) {
      return;
    }
    runSafe("bootstrap");
    interval = setInterval(() => {
      if (!cancelled) {
        runSafe("scheduled");
      }
    }, intervalMs);
    interval.unref?.();
  }, jitterDelay(jitterMs));
  bootstrap.unref?.();
  return {
    cancel() {
      cancelled = true;
      clearTimeout(bootstrap);
      if (interval) {
        clearInterval(interval);
      }
    },
  };
}

export function registerBillingReconciliationScheduler(input: {
  enabled: boolean;
  execution?: BillingImportExecutionMode;
  mode?: "automatic" | "manual";
  onError?: (error: unknown) => void;
  run: () => Promise<void>;
  schedule?: Partial<BillingReconciliationSchedule>;
}): { cancel(): void } | null {
  if (!shouldAutomaticallyImportBillingCustomers(input)) {
    return null;
  }
  return registerEmbeddedBillingScheduler({
    enabled: true,
    execution: input.execution,
    onError: input.onError,
    run: () => input.run(),
    schedule: input.schedule,
  });
}
